import React, { useState, useEffect } from "react";
import { clearPlaceholderBackground, bookImageSrc, bookImageFallback, bookImageBackground } from "./config";
import { useNavigate } from "react-router-dom";
import "../styles/Featuredbooks.css";

const FeaturedBooks = () => {
  const [topBooks, setTopBooks] = useState([]);
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [mostVisited, setMostVisited] = useState([]);
  const [freeBooks, setFreeBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const userRole = localStorage.getItem("userRole");
  const savedInterests = JSON.parse(localStorage.getItem("selectedInterests")) || [];
  const isSuperAdmin = userRole === "superadmin" || savedInterests.includes("All");

  useEffect(() => {
    fetchAllBooks();
  }, []);

  const fetchAllBooks = async () => {
    try {
      setLoading(true);
      
      const fetchData = async (url, source, account_id, reg_id) => {
        try {
          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({ account_id, reg_id }),
          });
          const data = await res.json();
          console.log(`✅ Fetched ${source}:`, data.data?.length || 0, "books");
          return Array.isArray(data.data)
            ? data.data.map((b) => ({ ...b, source }))
            : [];
        } catch (err) {
          console.error(`❌ Error fetching ${source}:`, err);
          return [];
        }
      };

      // Use the exact same endpoints as Book.js
      const sources = [
        {
          url: "https://midc.easylearn.org.in/AI_Controller/get_courseware_list",
          source: "Industry",
          account_id: "96",
          reg_id: "5"
        },
        {
          url: "https://technology.skillmaster.net.in/AI_Controller/get_courseware_list",
          source: "Technology",
          account_id: "96",
          reg_id: "5"
        },
        {
          url: "https://agriculture.skillmaster.net.in/AI_Controller/get_courseware_list",
          source: "Agriculture",
          account_id: "96",
          reg_id: "5"
        },
        {
          url: "https://mathematics.easylearn.net.in/AI_Controller/get_courseware_list",
          source: "Mathematics",
          account_id: "5",
          reg_id: "1"
        },
        {
          url: "https://vocational.skillmaster.net.in/AI_Controller/get_courseware_list",
          source: "Vocational",
          account_id: "96",
          reg_id: "5"
        },
        {
          url: "https://upsc.skillmaster.net.in/AI_Controller/get_courseware_list",
          source: "UPSC",
          account_id: "96",
          reg_id: "5"
        },
        {
          url: "https://k12.easylearn.org.in/AI_Controller/get_courseware_list",
          source: "K12",
          account_id: "96",
          reg_id: "5"
        },
        {
          url: "https://medical.skillmaster.net.in/AI_Controller/get_courseware_list",
          source: "Medical",
          account_id: "10",
          reg_id: "2"
        },
        {
          url: "https://management.skillmaster.net.in/AI_Controller/get_courseware_list",
          source: "Management",
          account_id: "31",
          reg_id: "5"
        },
        {
          url: "https://leadership.skillmaster.net.in/AI_Controller/get_courseware_list",
          source: "Leadership",
          account_id: "96",
          reg_id: "5"
        }
      ];

      console.log("🚀 Starting to fetch books from all sources...");

      // Fetch all books in parallel
      const allBooksArrays = await Promise.all(
        sources.map(({ url, source, account_id, reg_id }) =>
          fetchData(url, source, account_id, reg_id)
        )
      );

      // Flatten the array of arrays
      let allBooks = allBooksArrays.flat();

      console.log("📚 Total books fetched:", allBooks.length);

      if (allBooks.length === 0) {
        console.warn("⚠️ No books fetched from any source");
        setLoading(false);
        return;
      }

      // Shuffle and pick random books for different collections
      const shuffled = [...allBooks].sort(() => 0.5 - Math.random());
      
      // Get 10 random books for each section
      const top10 = shuffled.slice(0, 10);
      const featured = shuffled.slice(10, 20);
      const mostVisited = shuffled.slice(20, 30);
      
      console.log("📖 Top 10 books:", top10.length);
      console.log("⭐ Featured books:", featured.length);
      console.log("👁️ Most visited books:", mostVisited.length);
      
      setTopBooks(top10);
      setFeaturedBooks(featured);
      setMostVisited(mostVisited);
      
      // Get free books
      const freeBooksOnly = allBooks
        .filter(book => !book.price || book.price === "0" || book.price === 0)
        .sort(() => 0.5 - Math.random())
        .slice(0, 10);
      
      console.log("🆓 Free books:", freeBooksOnly.length);
      setFreeBooks(freeBooksOnly);

      console.log("✅ All collections loaded successfully!");
      setLoading(false);
    } catch (error) {
      console.error("❌ Error fetching books:", error);
      setLoading(false);
    }
  };

  const handleBookClick = (book) => {
    navigate("/book-detail", { state: { book } });
  };

  const handleViewAll = () => {
    navigate("/allpages");
  };

  const BookSection = ({ title, books }) => (
    <div className="featured-section">
      <div className="section-header">
        <h2 className="section-title">{title}</h2>
        <button className="view-all-btn" onClick={handleViewAll}>
          View All
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14m-7-7 7 7-7 7"/>
          </svg>
        </button>
      </div>
      {books.length === 0 ? (
        <div className="no-books-message">
          <p>No books available in this collection yet.</p>
          <button className="view-all-btn" onClick={handleViewAll} style={{ marginTop: "1rem" }}>
            Browse All Books
          </button>
        </div>
      ) : (
        <div className="books-carousel">
          {books.map((book, index) => {
            const isPurchased = JSON.parse(localStorage.getItem("purchasedBooks") || "[]")
              .some(item => item.pdf_name === book.pdf_name);
            
            return (
              <div key={`${book.id}-${index}`} className="featured-book-card" onClick={() => handleBookClick(book)}>
                <div className="featured-book-image-container" style={bookImageBackground(book, 240, 240)}>
                  {isPurchased ? (
                    <span className="book-badge purchased-badge">Purchased</span>
                  ) : book.price === "0" || book.price === 0 || !book.price ? (
                    <span className="book-badge free-badge">FREE</span>
                  ) : (
                    <span className="book-badge price-badge">₹{book.price}</span>
                  )}
                  <img
                    src={bookImageSrc(book, 240, 240)}
                    alt={book.pdf_name || "Book"}
                    className="featured-book-image"
                    onError={bookImageFallback(book, 240, 240)}
                    onLoad={clearPlaceholderBackground}
                  />
                  <div className="book-overlay">
                    <button className="quick-view-btn" onClick={(e) => { e.stopPropagation(); handleBookClick(book); }}>
                      Quick View
                    </button>
                  </div>
                </div>
                <div className="featured-book-info">
                  <h3 className="featured-book-title">{book.pdf_name || "Untitled Book"}</h3>
                  {book.rating && book.rating !== "0" && parseFloat(book.rating) > 0 && (
                    <div className="featured-book-rating">
                      <span className="rating-stars">★</span>
                      <span className="rating-value">{parseFloat(book.rating).toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="featured-books-page">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading amazing books for you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="featured-books-page">
      {/* Book Collections */}
      <div className="featured-container">
        <BookSection
          title="Free Books for You"
          books={freeBooks}
        />
        <BookSection
          title="Top 10 Books of All Time"
          books={topBooks}
        />

        <BookSection
          title="Featured Books"
          books={featuredBooks}
        />

        <BookSection
          title="Most Visited"
          books={mostVisited}
        />
      </div>
    </div>
  );
};

export default FeaturedBooks;